import { LitElement, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "@phosphor-icons/webcomponents/PhX";
import "@phosphor-icons/webcomponents/PhCalendar";
import "@phosphor-icons/webcomponents/PhClock";
import "@phosphor-icons/webcomponents/PhStop";
import "@phosphor-icons/webcomponents/PhWarning";
import "@phosphor-icons/webcomponents/PhCopy";
import "@phosphor-icons/webcomponents/PhBroadcast";
import "ui-components/button/wow-button";
import "ui-components/form-elements/input/wow-input";
import "ui-components/form-elements/textarea/textarea";
import "ui-components/form-elements/select/wow-select";
import "ui-components/media-uploader/media-uploader";
import "ui-components/modal/wow-modal";
import "ui-components/confirmation/wow-confirmation";
import { NotificationService } from "ui-components/notification";
// TODO: stream-stats was built for SRS, needs reimplementation for current streaming
// import "../stream-stats/stream-stats";
import "../stream-config-modal/stream-config-modal";
import "../live-stream-modal/live-stream-modal";

import { styles } from "./edit-live-event-modal.css";
import { rpc } from "ui-common/services/rpc";
import { WowFormController } from "ui-common/form/wow-form-controller";
import {
  createLiveEventValidators,
  MAX_ATTENDEES_LIMIT,
  MAX_PRICE_LIMIT,
} from "ui-common/form/validators/live-event-validators";
import type { LiveEventFormData } from "ui-common/form/validators/live-event-validators";
import { t, translate } from "../../translations/i18n";

@customElement("edit-live-event-modal")
export class EditLiveEventModal extends LitElement {
  static override styles = [styles];

  @property({ type: String })
  eventId!: string;

  @property({ type: Array })
  featureFlags: string[] = [];

  @state()
  private eventTitle: string = "";

  @state()
  private description = "";

  @state()
  private contentTier: "free" | "premium" = "premium";

  @state()
  private feeEuros = "";

  @state()
  private scheduledDate = "";

  @state()
  private scheduledTime = "";

  @state()
  private maxAttendees = "";

  @state()
  private thumbnailFileId?: string;

  @state()
  private thumbnailUrl?: string;

  @state()
  private whatsappUrl = "";

  @state()
  private postBuyingVideoUrl = "";

  @state()
  private postBuyingVideoFileId?: string;

  @state()
  private ctaText = "";

  @state()
  private backgroundImageFileIds: string[] = [];

  @state()
  private backgroundImageUrls: string[] = [];

  @state()
  private eventStatus: string = "";

  @state()
  private isSubmitting = false;

  @state()
  private isEnding = false;

  @state()
  private isLoadingEvent = false;

  @state()
  private showEndConfirmation = false;

  @state()
  private showGoLiveConfirmation = false;

  @state()
  private isGoingLive = false;

  @state()
  private showStopLiveConfirmation = false;

  @state()
  private isStopping = false;

  @state()
  private showStreamConfigModal = false;

  @state()
  private showLiveStreamModal = false;

  @state()
  private streamConfig: { aspectRatio: "landscape" | "portrait"; enableAudio: boolean } | null = null;

  private readonly RTMP_URL = "rtmp://stream.worldofwow.com/streams";
  private formController!: WowFormController<LiveEventFormData>;
  private hasInitializedForm = false;

  override connectedCallback() {
    super.connectedCallback();
    this.loadEventData();
  }

  private initializeFormController(): void {
    if (this.hasInitializedForm) return;

    const validators = createLiveEventValidators(translate as (key: string) => string);

    this.formController = new WowFormController<LiveEventFormData>(
      this,
      {
        title: this.eventTitle,
        description: this.description,
        feeEuros: this.feeEuros,
        scheduledDate: this.scheduledDate,
        scheduledTime: this.scheduledTime,
        maxAttendees: this.maxAttendees,
        whatsappUrl: this.whatsappUrl,
        ctaText: this.ctaText,
      },
      {
        validateOnBlur: true,
        validators: {
          title: [validators.validateTitle],
          description: [validators.validateDescription],
          feeEuros: [validators.createFeeValidator(() => this.contentTier)],
          scheduledDate: [validators.validateDate],
          scheduledTime: [validators.validateTime],
          maxAttendees: [validators.validateMaxAttendees],
        },
      },
      async (event) => {
        if (!event.isValid || !event.data) return;
        await this.submitFormData(event.data);
      },
      "#edit-live-event-form",
    );
    this.hasInitializedForm = true;
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
  }

  private async loadEventData() {
    if (!this.eventId) return;

    try {
      this.isLoadingEvent = true;

      const result = await rpc("get-live-event-by-id", { event_id: this.eventId });

      if (!result.event) {
        NotificationService.error(translate("editLiveEventModal.eventNotFound"));
        return;
      }

      const event = result.event;

      // Pre-fill form fields
      this.eventTitle = event.title || "";
      this.description = event.description || "";
      this.contentTier = event.content_tier === "premium" ? "premium" : "free";
      this.feeEuros = event.fee_cents ? (event.fee_cents / 100).toFixed(2) : "";
      this.maxAttendees = event.max_attendees ? event.max_attendees.toString() : "";
      this.thumbnailFileId = event.thumbnail_file_id || undefined;
      this.thumbnailUrl = event.thumbnail_url || undefined;
      this.whatsappUrl = event.whatsapp_url || "";
      this.postBuyingVideoFileId = event.post_buying_video_file_id || undefined;
      this.postBuyingVideoUrl = event.post_buying_video_url || "";
      this.ctaText = event.cta_text || "";
      this.backgroundImageFileIds = event.background_image_file_ids || [];
      this.backgroundImageUrls = event.background_image_urls || [];
      this.eventStatus = event.status || "";

      // Parse date and time from ISO string (use local time consistently)
      if (event.scheduled_start_time) {
        const date = new Date(event.scheduled_start_time);
        // Format as YYYY-MM-DD for date input (local time)
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const day = date.getDate().toString().padStart(2, "0");
        this.scheduledDate = `${year}-${month}-${day}`;
        // Format as HH:mm for time input (local time)
        const hours = date.getHours().toString().padStart(2, "0");
        const minutes = date.getMinutes().toString().padStart(2, "0");
        this.scheduledTime = `${hours}:${minutes}`;
      }

      // Initialize form controller after data is loaded
      this.initializeFormController();
    } catch (error) {
      console.error("Error loading event:", error);
      NotificationService.error(translate("editLiveEventModal.failedToLoad"));
    } finally {
      this.isLoadingEvent = false;
    }
  }

  private async submitFormData(data: LiveEventFormData): Promise<void> {
    this.isSubmitting = true;

    try {
      // Parse as local time to validate
      const scheduledDateTime = new Date(`${data.scheduledDate}T${data.scheduledTime}`);

      if (isNaN(scheduledDateTime.getTime())) {
        NotificationService.error(translate("editLiveEventModal.invalidDateTime"));
        this.isSubmitting = false;
        return;
      }

      if (scheduledDateTime <= new Date()) {
        NotificationService.error(translate("editLiveEventModal.dateMustBeFuture"));
        this.isSubmitting = false;
        return;
      }

      const localDateTime = new Date(`${data.scheduledDate}T${data.scheduledTime}:00`);
      const isoString = localDateTime.toISOString();

      const updateParams = {
        event_id: this.eventId,
        title: data.title.trim(),
        description: data.description.trim(),
        content_tier: this.contentTier,
        fee_cents: this.contentTier === "premium" && data.feeEuros ? Math.round(parseFloat(data.feeEuros) * 100) : null,
        scheduled_start_time: isoString,
        max_attendees: data.maxAttendees ? Number(data.maxAttendees) : null,
        thumbnail_file_id: this.thumbnailFileId || null,
        whatsapp_url: data.whatsappUrl?.trim() || null,
        post_buying_video_file_id: this.postBuyingVideoFileId || null,
        cta_text: data.ctaText?.trim() || null,
        background_image_file_ids: this.backgroundImageFileIds.length > 0 ? this.backgroundImageFileIds : null,
      };

      const result = await rpc("update-live-event", updateParams);

      if (result && typeof result === "object" && "success" in result && result.success) {
        NotificationService.success(translate("editLiveEventModal.updateSuccess"));

        // Dispatch event to refresh the list
        this.dispatchEvent(
          new CustomEvent("event-updated", {
            detail: { event: (result as { event?: unknown }).event },
            bubbles: true,
          }),
        );

        // Close modal after a short delay
        setTimeout(() => {
          this.handleClose();
        }, 500);
      } else {
        const message =
          result && typeof result === "object" && "message" in result
            ? (result as { message?: string }).message || translate("editLiveEventModal.failedToUpdate")
            : translate("editLiveEventModal.failedToUpdate");
        NotificationService.error(message);
      }
    } catch {
      NotificationService.error(translate("editLiveEventModal.failedToUpdate"));
    } finally {
      this.isSubmitting = false;
    }
  }

  private async handleSubmit(): Promise<void> {
    if (!this.formController) {
      NotificationService.error("Form not initialized properly");
      return;
    }

    // Use form controller to submit
    this.formController.submit();
  }

  private handleEndEventClick() {
    this.showEndConfirmation = true;
  }

  private handleCancelEnd() {
    this.showEndConfirmation = false;
  }

  private async handleConfirmEnd() {
    try {
      this.isEnding = true;
      this.showEndConfirmation = false;
      const result = await rpc("end-live-event", { event_id: this.eventId });

      if (result.success) {
        NotificationService.success(translate("editLiveEventModal.endEventSuccess"));

        this.dispatchEvent(
          new CustomEvent("event-ended", {
            detail: { eventId: this.eventId },
            bubbles: true,
            composed: true,
          }),
        );

        setTimeout(() => {
          this.handleClose();
        }, 1000);
      } else {
        NotificationService.error(translate("editLiveEventModal.endEventFailed"));
      }
    } catch (error) {
      console.error("Error ending event:", error);
      NotificationService.error(translate("editLiveEventModal.endEventFailed"));
    } finally {
      this.isEnding = false;
    }
  }

  private handleClose() {
    this.dispatchEvent(new CustomEvent("modal-close", { bubbles: true }));
  }

  private handleGoLiveClick() {
    this.showGoLiveConfirmation = true;
  }

  private handleCancelGoLive() {
    this.showGoLiveConfirmation = false;
  }

  private async handleConfirmGoLive() {
    try {
      this.isGoingLive = true;
      this.showGoLiveConfirmation = false;
      const result = await rpc("start-live-event", { event_id: this.eventId });

      if (result.success) {
        NotificationService.success(translate("editLiveEventModal.goLiveSuccess"));
        this.eventStatus = "live";

        this.dispatchEvent(
          new CustomEvent("event-updated", {
            detail: { eventId: this.eventId },
            bubbles: true,
            composed: true,
          }),
        );
      } else {
        NotificationService.error(translate("editLiveEventModal.goLiveFailed"));
      }
    } catch (error) {
      console.error("Error starting live event:", error);
      NotificationService.error(translate("editLiveEventModal.goLiveFailed"));
    } finally {
      this.isGoingLive = false;
    }
  }

  private handleStopLiveClick() {
    this.showStopLiveConfirmation = true;
  }

  private handleCancelStopLive() {
    this.showStopLiveConfirmation = false;
  }

  private async handleConfirmStopLive() {
    try {
      this.isStopping = true;
      this.showStopLiveConfirmation = false;
      const result = await rpc("stop-live-event", { event_id: this.eventId });

      if (result.success) {
        NotificationService.success(translate("editLiveEventModal.stopLiveSuccess"));
        this.eventStatus = "scheduled";

        this.dispatchEvent(
          new CustomEvent("event-updated", {
            detail: { eventId: this.eventId },
            bubbles: true,
            composed: true,
          }),
        );
      } else {
        NotificationService.error(translate("editLiveEventModal.stopLiveFailed"));
      }
    } catch (error) {
      console.error("Error stopping live event:", error);
      NotificationService.error(translate("editLiveEventModal.stopLiveFailed"));
    } finally {
      this.isStopping = false;
    }
  }

  private handleThumbnailUpload(e: CustomEvent) {
    const { fileId } = e.detail;
    this.thumbnailFileId = fileId;
  }

  private handleVideoUpload(e: CustomEvent) {
    const { fileId } = e.detail;
    this.postBuyingVideoFileId = fileId;
  }

  private handleBackgroundImageUpload(index: number, e: CustomEvent) {
    const { fileId, fileUrl } = e.detail;
    const newFileIds = [...this.backgroundImageFileIds];
    const newUrls = [...this.backgroundImageUrls];

    if (fileId) {
      newFileIds[index] = fileId;
      newUrls[index] = fileUrl || "";
    } else {
      newFileIds.splice(index, 1);
      newUrls.splice(index, 1);
    }

    this.backgroundImageFileIds = newFileIds.filter(Boolean);
    this.backgroundImageUrls = newUrls.filter(Boolean);
  }

  private get hasBothLiveEventFlags(): boolean {
    return this.featureFlags.includes("open-live-events") && this.featureFlags.includes("paid-live-events");
  }

  private get todayDate(): string {
    return new Date().toISOString().split("T")[0]!;
  }

  private get hasBackgroundImagesFlag(): boolean {
    return this.featureFlags.includes("live-event-background-images");
  }

  private handleStartStreamingClick(): void {
    this.showStreamConfigModal = true;
  }

  private handleStreamConfigCancel(): void {
    this.showStreamConfigModal = false;
  }

  private handleStartStream(e: CustomEvent): void {
    this.streamConfig = e.detail;
    this.showStreamConfigModal = false;
    this.showLiveStreamModal = true;
  }

  private handleStreamEnded(): void {
    this.showLiveStreamModal = false;
    this.streamConfig = null;
  }

  private async copyToClipboard(text: string, label: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      NotificationService.success(`${label} copied to clipboard`);
    } catch {
      NotificationService.error(`Failed to copy ${label}`);
    }
  }

  protected override render() {
    // Check if scheduled time has passed
    const hasScheduledTimePassed =
      this.scheduledDate && this.scheduledTime
        ? new Date(`${this.scheduledDate}T${this.scheduledTime}`).getTime() <= Date.now()
        : false;

    const isLive = this.eventStatus === "live";
    const isEnded = this.eventStatus === "ended";

    // Show end button if event is actually live OR if scheduled time has passed (so creator can mark it as ended)
    const canEndEvent = isLive || (this.eventStatus === "scheduled" && hasScheduledTimePassed);

    return html`
      <wow-modal
        mobile-fullscreen
        ?disable-primary-button=${this.isSubmitting || isEnded || this.isLoadingEvent}
        @close=${this.handleClose}
        @footer-button-clicked=${this.handleSubmit}
      >
        <span slot="header">
          ${this.isLoadingEvent
            ? t("editLiveEventModal.headerLoading")
            : isEnded
              ? t("editLiveEventModal.headerEnded")
              : isLive
                ? t("editLiveEventModal.headerLive")
                : t("editLiveEventModal.headerEdit")}
        </span>

        <div class="form-content">
          ${this.isLoadingEvent
            ? html`<div style="text-align: center; padding: 40px;">${t("editLiveEventModal.loadingEventData")}</div>`
            : html`
                <form id="edit-live-event-form" class="form-content">
                  <!-- Event Cover Image -->
                  <media-uploader
                    responsive
                    context="cover_image"
                    label="${t("editLiveEventModal.firstThumb")}"
                    viewport="cover"
                    currentFileUrl=${this.thumbnailUrl}
                    @change=${this.handleThumbnailUpload}
                  ></media-uploader>

                  <!-- Title -->
                  <wow-input
                    name=${this.formController.fieldNames.title}
                    label="${t("editLiveEventModal.title")}"
                    placeholder="${t("editLiveEventModal.titlePlaceholder")}"
                    required
                  ></wow-input>

                  <!-- Description -->
                  <wow-textarea
                    name=${this.formController.fieldNames.description}
                    label="${t("editLiveEventModal.description")}"
                    placeholder="${t("editLiveEventModal.descriptionPlaceholder")}"
                    rows="3"
                    maxlength="750"
                    required
                  ></wow-textarea>

                  ${this.hasBothLiveEventFlags
                    ? html`
                        <wow-select
                          label="${t("editLiveEventModal.accessLevel")}"
                          .options=${[
                            { value: "free", label: translate("editLiveEventModal.accessLevelFree") },
                            { value: "premium", label: translate("editLiveEventModal.accessLevelPremium") },
                          ]}
                          .value=${this.contentTier}
                          @change=${(e: CustomEvent) => {
                            this.contentTier = e.detail.value;
                          }}
                        ></wow-select>
                      `
                    : nothing}
                  ${this.contentTier === "premium"
                    ? html`
                        <wow-input
                          name=${this.formController.fieldNames.feeEuros}
                          type="number"
                          inputmode="decimal"
                          label="${t("createLiveEventsModal.price")}"
                          placeholder="${t("createLiveEventsModal.pricePlaceholder")}"
                          help-text=${t("createLiveEventsModal.priceHelper")}
                          min="0.50"
                          max="${MAX_PRICE_LIMIT}"
                          step="0.01"
                          pattern="^\\d+(\\.\\d{1,2})?$"
                          required
                        ></wow-input>
                      `
                    : nothing}

                  <!-- Date and Time -->
                  <div class="form-row">
                    <wow-input
                      name=${this.formController.fieldNames.scheduledDate}
                      type="date"
                      label="${t("editLiveEventModal.date")}"
                      min=${this.todayDate}
                      help-text=${isEnded ? translate("editLiveEventModal.dateCannotChange") : ""}
                      ?disabled=${isEnded}
                      required
                    ></wow-input>

                    <wow-input
                      name=${this.formController.fieldNames.scheduledTime}
                      type="time"
                      label="${t("editLiveEventModal.time")}"
                      help-text=${isEnded ? translate("editLiveEventModal.timeCannotChange") : ""}
                      ?disabled=${isEnded}
                      required
                    ></wow-input>
                  </div>

                  <!-- Max Attendees -->
                  <wow-input
                    name=${this.formController.fieldNames.maxAttendees}
                    type="number"
                    inputmode="numeric"
                    label="${t("editLiveEventModal.maxAttendees")}"
                    placeholder="${t("editLiveEventModal.maxAttendeesPlaceholder")}"
                    step="1"
                    min="1"
                    max="${MAX_ATTENDEES_LIMIT}"
                  ></wow-input>

                  <!-- WhatsApp URL -->
                  <wow-input
                    name=${this.formController.fieldNames.whatsappUrl}
                    type="url"
                    label="${t("editLiveEventModal.whatsApp")}"
                    placeholder="${t("editLiveEventModal.whatsAppPlaceholder")}"
                  ></wow-input>

                  <!-- Post-Purchase Media -->
                  <media-uploader
                    responsive
                    context="cover_image"
                    label="${t("editLiveEventModal.secondThumb")}"
                    viewport="post"
                    currentFileUrl=${this.postBuyingVideoUrl}
                    @change=${this.handleVideoUpload}
                  ></media-uploader>

                  <!-- CTA Text -->
                  <wow-input
                    name=${this.formController.fieldNames.ctaText}
                    label="${t("editLiveEventModal.ctaButton")}"
                    placeholder="${t("editLiveEventModal.ctaButtonPlaceholder")}"
                    maxlength="255"
                  ></wow-input>

                  <!-- Background Images -->
                  ${this.hasBackgroundImagesFlag
                    ? html`
                        <div class="background-images-section">
                          <label class="section-label">${t("editLiveEventModal.bgImage")}</label>
                          <div class="background-images-grid">
                            ${[0, 1, 2, 3].map(
                              (index) => html`
                                <media-uploader
                                  context="cover_image"
                                  viewport="post"
                                  currentFileUrl=${this.backgroundImageUrls[index] || ""}
                                  @change=${(e: CustomEvent) => this.handleBackgroundImageUpload(index, e)}
                                ></media-uploader>
                              `,
                            )}
                          </div>
                        </div>
                      `
                    : nothing}

                  <!-- Stream Test Section (always visible for non-ended events) -->
                  ${!isEnded
                    ? html`
                        <div class="stream-test-section">
                          <div class="stream-test-header">${t("editLiveEventModal.streamTestHeader")}</div>
                          <div class="obs-info-section">
                            <div class="obs-info-header">${t("editLiveEventModal.streamFromObs")}</div>
                            <div class="stream-info-row">
                              <span class="stream-info-label">${t("editLiveEventModal.streamUrl")}</span>
                              <code class="stream-info-value">${this.RTMP_URL}</code>
                              <wow-button
                                variant="subtle"
                                size="small"
                                square
                                @click=${() => this.copyToClipboard(this.RTMP_URL, "URL")}
                              >
                                <ph-copy size="16"></ph-copy>
                              </wow-button>
                            </div>
                            <div class="stream-info-row">
                              <span class="stream-info-label">${t("editLiveEventModal.streamKey")}</span>
                              <code class="stream-info-value">${this.eventId}</code>
                              <wow-button
                                variant="subtle"
                                size="small"
                                square
                                @click=${() =>
                                  this.copyToClipboard(
                                    this.eventId,
                                    translate("editLiveEventModal.streamKey").replace(":", ""),
                                  )}
                              >
                                <ph-copy size="16"></ph-copy>
                              </wow-button>
                            </div>
                          </div>
                          <wow-button variant="primary" size="medium" @click=${this.handleStartStreamingClick}>
                            <ph-broadcast size="18" weight="fill"></ph-broadcast>
                            ${t("editLiveEventModal.startStreaming")}
                          </wow-button>
                          ${this.eventStatus === "scheduled"
                            ? html`
                                <wow-button
                                  variant="danger"
                                  size="medium"
                                  @click=${this.handleGoLiveClick}
                                  ?disabled=${this.isGoingLive}
                                >
                                  <ph-broadcast size="18" weight="fill"></ph-broadcast>
                                  ${this.isGoingLive
                                    ? t("editLiveEventModal.goingLive")
                                    : t("editLiveEventModal.goLive")}
                                </wow-button>
                              `
                            : nothing}
                          ${isLive
                            ? html`
                                <wow-button
                                  variant="neutral"
                                  size="medium"
                                  @click=${this.handleStopLiveClick}
                                  ?disabled=${this.isStopping}
                                >
                                  <ph-stop size="18" weight="fill"></ph-stop>
                                  ${this.isStopping
                                    ? t("editLiveEventModal.stoppingLive")
                                    : t("editLiveEventModal.stopLive")}
                                </wow-button>
                              `
                            : nothing}
                        </div>
                      `
                    : nothing}

                  <!-- End Event Section (show if event can be ended) -->
                  ${canEndEvent
                    ? html`
                        <div class="end-event-section">
                          <div class="warning-banner">
                            <ph-warning size="20" weight="fill"></ph-warning>
                            <span
                              >${isLive
                                ? t("editLiveEventModal.eventLiveWarning")
                                : t("editLiveEventModal.eventPassedWarning")}</span
                            >
                          </div>
                          <wow-button
                            variant="danger"
                            size="medium"
                            @click=${this.handleEndEventClick}
                            ?disabled=${this.isEnding}
                          >
                            <ph-stop size="18" weight="fill"></ph-stop>
                            ${this.isEnding ? t("editLiveEventModal.ending") : t("editLiveEventModal.endEvent")}
                          </wow-button>
                        </div>
                      `
                    : nothing}

                  <!-- Stream Statistics - TODO: needs reimplementation for current streaming (was built for SRS) -->
                </form>
              `}
        </div>

        ${!isEnded && !this.isLoadingEvent
          ? html`<span slot="footer-button-text"
              >${this.isSubmitting ? t("editLiveEventModal.updating") : t("editLiveEventModal.submitButton")}</span
            >`
          : nothing}
      </wow-modal>
      ${this.showEndConfirmation
        ? html`
            <wow-confirmation header="${translate("editLiveEventModal.endEventHeader")}">
              <div class="delete-confirmation-content">
                <p>${t("editLiveEventModal.endEventConfirm")}</p>
                <div class="confirmation-buttons">
                  <wow-button variant="danger" size="medium" @click=${this.handleConfirmEnd}>
                    ${t("editLiveEventModal.endEventButton")}
                  </wow-button>
                  <wow-button variant="neutral" size="medium" @click=${this.handleCancelEnd}>
                    ${t("editLiveEventModal.endEventCancel")}
                  </wow-button>
                </div>
              </div>
            </wow-confirmation>
          `
        : nothing}
      ${this.showGoLiveConfirmation
        ? html`
            <wow-confirmation header="${translate("editLiveEventModal.goLiveHeader")}">
              <div class="delete-confirmation-content">
                <p>${t("editLiveEventModal.goLiveConfirm")}</p>
                <div class="confirmation-buttons">
                  <wow-button variant="danger" size="medium" @click=${this.handleConfirmGoLive}>
                    ${t("editLiveEventModal.goLiveButton")}
                  </wow-button>
                  <wow-button variant="neutral" size="medium" @click=${this.handleCancelGoLive}>
                    ${t("editLiveEventModal.goLiveCancel")}
                  </wow-button>
                </div>
              </div>
            </wow-confirmation>
          `
        : nothing}
      ${this.showStopLiveConfirmation
        ? html`
            <wow-confirmation header="${translate("editLiveEventModal.stopLiveHeader")}">
              <div class="delete-confirmation-content">
                <p>${t("editLiveEventModal.stopLiveConfirm")}</p>
                <div class="confirmation-buttons">
                  <wow-button variant="neutral" size="medium" @click=${this.handleConfirmStopLive}>
                    ${t("editLiveEventModal.stopLiveButton")}
                  </wow-button>
                  <wow-button variant="neutral" size="medium" @click=${this.handleCancelStopLive}>
                    ${t("editLiveEventModal.stopLiveCancel")}
                  </wow-button>
                </div>
              </div>
            </wow-confirmation>
          `
        : nothing}
      ${this.showStreamConfigModal
        ? html`
            <stream-config-modal
              .streamId=${this.eventId}
              @start-stream=${this.handleStartStream}
              @cancel=${this.handleStreamConfigCancel}
            ></stream-config-modal>
          `
        : nothing}
      ${this.showLiveStreamModal && this.streamConfig
        ? html`
            <live-stream-modal
              .streamId=${this.eventId}
              aspect-ratio=${this.streamConfig.aspectRatio}
              ?enable-audio=${this.streamConfig.enableAudio}
              @stream-ended=${this.handleStreamEnded}
            ></live-stream-modal>
          `
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "edit-live-event-modal": EditLiveEventModal;
  }
}
